import { useState, useMemo, useId } from 'react';
import { getApiBase } from './apiBase';

function apiFetch(path, options = {}) {
  const url = `${getApiBase()}${path}`;
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
}

export default function ExternalIdentifierLookup({ connectionsList = [], selectedConnectionId }) {
  const inputId = useId();
  const activeConnectionId = useMemo(() => {
    if (!connectionsList?.length) return null;
    if (selectedConnectionId && connectionsList.some((c) => c.id === selectedConnectionId)) {
      return selectedConnectionId;
    }
    return connectionsList[0]?.id ?? null;
  }, [connectionsList, selectedConnectionId]);

  const activeConnection = connectionsList?.find((c) => c.id === activeConnectionId);

  const [testExternalId, setTestExternalId] = useState('');
  const [testingConnectionId, setTestingConnectionId] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const runLookup = async () => {
    if (!activeConnection) return;
    setTestingConnectionId(activeConnection.id);
    setTestResult(null);
    try {
      const res = await apiFetch(`/api/connections/${activeConnection.id}/test`, {
        method: 'POST',
        body: JSON.stringify({
          externalId: testExternalId.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Lookup failed (${res.status})`);
      }
      setTestResult({ connectionId: activeConnection.id, data });
    } catch (err) {
      setTestResult({ connectionId: activeConnection.id, error: err.message });
    } finally {
      setTestingConnectionId(null);
    }
  };

  if (!connectionsList?.length) {
    return (
      <div className="rounded-xl border border-dashed border-cyan-600/45 bg-gradient-to-br from-slate-700 to-cyan-900 p-4 text-sm text-cyan-100/90 shadow-md ring-1 ring-cyan-700/35">
        Add a saved connection (sidebar → Connections) to look up Oracle person numbers from an external identifier.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-cyan-600/50 bg-gradient-to-br from-slate-700 via-cyan-900/95 to-teal-900 p-4 shadow-md ring-1 ring-cyan-600/25">
      {connectionsList.length > 1 && activeConnection && (
        <p className="mb-3 text-xs text-cyan-200/85">
          Connection:{' '}
          <strong className="text-white">{activeConnection.connection_name || 'Unnamed'}</strong>
          {activeConnection.application_hcm_url && (
            <span className="mt-0.5 block truncate font-mono text-[11px] text-cyan-300/70" title={activeConnection.application_hcm_url}>
              {activeConnection.application_hcm_url}
            </span>
          )}
        </p>
      )}
      <label htmlFor={inputId} className="mb-1 block text-xs font-medium text-white">
        External Identifier
      </label>
      <div className="flex gap-2">
        <input
          id={inputId}
          type="text"
          value={testExternalId}
          onChange={(e) => setTestExternalId(e.target.value)}
          placeholder="e.g. 38994490"
          className="min-w-0 flex-1 rounded-lg border border-slate-600/80 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-inner focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
        />
        <button
          type="button"
          onClick={runLookup}
          disabled={testingConnectionId === activeConnection?.id}
          className="shrink-0 rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-cyan-400/40 hover:bg-cyan-500 disabled:opacity-50"
        >
          {testingConnectionId === activeConnection?.id ? 'Getting…' : 'Get Person Number'}
        </button>
      </div>
      {testResult && testResult.connectionId === activeConnection?.id && (testResult.error || testResult.data?.lookup) && (
        <div
          className={`mt-3 rounded-lg border p-3 text-xs ${
            testResult.error
              ? 'border-red-800/80 bg-red-950/50 text-red-100'
              : 'border-emerald-700/60 bg-emerald-950/40 text-emerald-100'
          }`}
        >
          {testResult.error ? (
            <p>{testResult.error}</p>
          ) : (
            <p>
              <strong>Person number:</strong> {testResult.data.lookup.personNumber ?? 'Not found'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
